import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { BulkProposalCard } from './BulkProposalCard';
import { PartnerAction, type MaintainPartnersPayload } from './proposal-part.types';

interface Props {
  proposalId: string;
  payload: MaintainPartnersPayload;
  resolved?: string | undefined;
  onConfirm: (proposalId: string) => void;
  onCancel: (proposalId: string) => void;
}

/** Static keys per operation, so the translations stay statically verifiable. */
const LABEL_KEYS = {
  [PartnerAction.ARCHIVE]: 'archive',
  [PartnerAction.UNARCHIVE]: 'unarchive',
  [PartnerAction.MARK_SUPPLIER]: 'markSupplier',
  [PartnerAction.MARK_CUSTOMER]: 'markCustomer'
} as const;

/**
 * Confirmation card for bulk partner housekeeping. The change is named in the title and
 * every affected partner is listed — one card never mixes two different changes.
 */
export function MaintainPartnersProposalCard({
  proposalId,
  payload,
  resolved,
  onConfirm,
  onCancel
}: Props) {
  const t = useFeatureTranslation('aiChat');
  const key = LABEL_KEYS[payload.action];

  return (
    <BulkProposalCard
      title={t(`proposals.maintain-partners.${key}.title`)}
      description={t(`proposals.maintain-partners.${key}.countDescription`, {
        count: payload.partners.length
      })}
      proposalId={proposalId}
      rows={payload.partners}
      rowKey={row => row.partnerId}
      primary={row => row.name}
      secondary={row =>
        row.companyId ? <span className='text-foreground-muted'>{row.companyId}</span> : null
      }
      resolved={resolved}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
